import { useMutation, useQuery } from '@tanstack/react-query';
import { CLAIM_KINDS, CLAIM_KIND_LABELS, ENTITY_TYPE_LABELS, OUTCOME_PATHS, OUTCOME_PATH_LABELS, SOLUTION_STATUS_LABELS, type ClaimKind, type OutcomePath } from '@raro/domain';
import { Button, EmptyState, Field, Input, Label, Notice, Select, Serif, Tag, Textarea, cx } from '@raro/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { useAiStatus } from '../lib/ai';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import type { ClaimRef, EntitySummary, Solution, SolutionClaim } from '../lib/types';
import { EntityPicker } from './EntityPicker';
import { useVerInvalidate } from './VerSections';

/**
 * A SOLUÇÃO: o produto do VER. Uma posição executável e aferível, sustentada por afirmações
 * com proveniência. A Machine rascunha; a equipe revisa afirmação por afirmação; um sócio aprova
 * e escolhe o caminho (o cliente executa, a Raro executa, híbrido, CONTÍNUO, LIVE).
 */
export function SolutionSection({ projectId, canEdit, canApprove }: { projectId: string; canEdit: boolean; canApprove: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const status = useAiStatus();
  const sol = useQuery({ queryKey: ['solution', projectId], queryFn: () => api.get<Solution | null>(`/projects/${projectId}/solution`) });
  const [manual, setManual] = useState(false);
  const [f, setF] = useState({ position: '', summary: '' });
  const [approving, setApproving] = useState(false);
  const [path, setPath] = useState<OutcomePath>('hybrid');
  const [publish, setPublish] = useState(true);
  const [editingPos, setEditingPos] = useState<string | null>(null);
  const [addingClaim, setAddingClaim] = useState(false);
  const draft = useMutation({ mutationFn: () => api.post<Solution & { latencyMs: number }>(`/projects/${projectId}/solution/draft`), onSuccess: invalidate });
  const create = useMutation({ mutationFn: () => api.post(`/projects/${projectId}/solution`, { position: f.position, summary: f.summary || undefined }), onSuccess: () => { setManual(false); invalidate(); } });
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/solutions/${sol.data!.id}`, body), onSuccess: () => { setEditingPos(null); invalidate(); } });
  const approve = useMutation({ mutationFn: () => api.post(`/solutions/${sol.data!.id}/approve`, { outcomePath: path, publish }), onSuccess: () => { setApproving(false); invalidate(); } });
  const llm = Boolean(status.data?.llm);
  const s = sol.data ?? null;
  const locked = s ? s.status === 'approved' || s.status === 'published' : false;
  const pendingClaims = s?.claims.filter((c) => c.humanStatus === 'under_review').length ?? 0;
  const accepted = s?.claims.filter((c) => c.humanStatus === 'accepted') ?? [];

  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>A Solução</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>o que sai do VER: uma posição executável e aferível, não um relatório. cada afirmação diz de onde vem.</span>
      </div>

      {sol.isSuccess && !s && (
        <div className="rr-card rr-stack rr-stack--4">
          <div className="rr-stack rr-stack--1">
            <span className="rr-heading">a Machine rascunha a Solução</span>
            <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm) ' }}>a partir da pergunta do ciclo, das hipóteses seguidas pelos sócios, das evidências e das contradições. as afirmações já chegam ligadas ao que as sustenta e ao que as contradiz. nada nasce aprovado.</span>
            {status.data && !llm && <span className="rr-field__error">sem modelo configurado. defina ANTHROPIC_API_KEY (ou AI_LLM_PROVIDER=mock).</span>}
          </div>
          {canEdit && (
            <div className="rr-row">
              <Button variant="primary" onClick={() => draft.mutate()} disabled={!llm || draft.isPending}>{draft.isPending ? 'a Machine está escrevendo…' : 'rascunhar com a Machine →'}</Button>
              {!manual && <Button variant="quiet" onClick={() => setManual(true)}>escrever à mão</Button>}
            </div>
          )}
          {draft.isError && <Notice tone="error">{(draft.error as Error).message}</Notice>}
          {manual && (
            <div className="rr-stack rr-stack--3">
              <Field label="A posição da Raro, em uma frase" htmlFor="sol-p"><Input id="sol-p" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} autoFocus /></Field>
              <Field label="Resumo" htmlFor="sol-s"><Textarea id="sol-s" rows={3} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} /></Field>
              {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
              <div className="rr-row"><Button variant="primary" disabled={f.position.trim().length < 3 || create.isPending} onClick={() => create.mutate()}>criar</Button><Button variant="quiet" onClick={() => setManual(false)}>cancelar</Button></div>
            </div>
          )}
        </div>
      )}

      {s && (
        <article className={cx('rr-solution', locked && 'rr-solution--approved')}>
          <div className="rr-row rr-row--between rr-row--wrap">
            <span className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
              <Tag tone={locked ? 'accent' : 'outline'}>{SOLUTION_STATUS_LABELS[s.status]}</Tag>
              <span>v{s.version} · {s.generatedBy === 'ai' ? `rascunho de ${s.model ?? 'Machine'}` : 'escrita por pessoa'} · {formatDate(s.createdAt)}</span>
              {s.approvedAt && <span>· aprovada em {formatDate(s.approvedAt)}</span>}
            </span>
            <div className="rr-row">
              {canEdit && !locked && <Button size="sm" variant="quiet" onClick={() => draft.mutate()} disabled={!llm || draft.isPending}>{draft.isPending ? 'reescrevendo…' : 'nova versão pela Machine'}</Button>}
              {canApprove && !locked && !approving && <Button size="sm" variant="primary" onClick={() => setApproving(true)} disabled={pendingClaims > 0}>{pendingClaims > 0 ? `${pendingClaims} afirmação(ões) a revisar` : 'aprovar (sócio)'}</Button>}
              {canEdit && !canApprove && !locked && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>um sócio aprova</span>}
            </div>
          </div>
          {draft.isError && <Notice tone="error">{(draft.error as Error).message}</Notice>}

          {editingPos !== null ? (
            <div className="rr-stack rr-stack--2">
              <Textarea rows={2} value={editingPos} onChange={(e) => setEditingPos(e.target.value)} autoFocus />
              <div className="rr-row"><Button size="sm" variant="primary" onClick={() => update.mutate({ position: editingPos })} disabled={editingPos.trim().length < 3 || update.isPending}>salvar</Button><Button size="sm" variant="quiet" onClick={() => setEditingPos(null)}>cancelar</Button></div>
            </div>
          ) : (
            <p className="rr-solution__position" onDoubleClick={() => canEdit && !locked && setEditingPos(s.position)}><Serif italic>{s.position}</Serif></p>
          )}
          {s.summary && <p className="rr-muted">{s.summary}</p>}

          {approving && (
            <div className="rr-card rr-stack rr-stack--3" style={{ borderColor: 'var(--rr-accent)' }}>
              <Label tone="strong">Bater o martelo</Label>
              <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>a posição vira o fechamento do ciclo. escolha o caminho: quem executa o que a Solução propõe?</p>
              <Field label="Caminho depois do VER" htmlFor="sol-path">
                <Select id="sol-path" value={path} onChange={(e) => setPath(e.target.value as OutcomePath)}>{OUTCOME_PATHS.map((p) => <option key={p} value={p}>{OUTCOME_PATH_LABELS[p]}</option>)}</Select>
              </Field>
              <label className="rr-row" style={{ gap: 'var(--rr-space-2)', fontSize: 'var(--rr-text-sm)' }}><input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} /> publicar na Sala do cliente (posição e afirmações aceitas)</label>
              {approve.isError && <Notice tone="error">{(approve.error as Error).message}</Notice>}
              <div className="rr-row"><Button variant="primary" onClick={() => approve.mutate()} disabled={approve.isPending}>aprovar a Solução</Button><Button variant="quiet" onClick={() => setApproving(false)}>voltar</Button></div>
            </div>
          )}

          <div className="rr-stack rr-stack--3">
            <div className="rr-row rr-row--between">
              <Label>Afirmações · {accepted.length} aceitas{pendingClaims ? ` · ${pendingClaims} a revisar` : ''}</Label>
              {canEdit && !locked && !addingClaim && <Button size="sm" variant="quiet" onClick={() => setAddingClaim(true)}>+ afirmação</Button>}
            </div>
            {addingClaim && <ClaimForm solutionId={s.id} projectId={projectId} onDone={() => { setAddingClaim(false); invalidate(); }} onCancel={() => setAddingClaim(false)} />}
            {s.claims.length === 0 && <EmptyState title="nenhuma afirmação.">o que vimos, o que passa a valer, o que fazer. cada uma com o que sustenta e o que contradiz.</EmptyState>}
            {(['finding', 'principle', 'recommendation'] as ClaimKind[]).map((kind) => {
              const rows = s.claims.filter((c) => c.kind === kind);
              if (rows.length === 0) return null;
              return (
                <div key={kind} className="rr-stack rr-stack--2">
                  <span className="rr-label rr-label--strong">{CLAIM_KIND_LABELS[kind]}</span>
                  {rows.map((c) => <ClaimCard key={c.id} c={c} projectId={projectId} canReview={canApprove && !locked} canEdit={canEdit && !locked} />)}
                </div>
              );
            })}
          </div>
        </article>
      )}
    </section>
  );
}

function ClaimForm({ solutionId, projectId, onDone, onCancel }: { solutionId: string; projectId: string; onDone: () => void; onCancel: () => void }) {
  const [f, setF] = useState({ kind: 'finding' as ClaimKind, text: '', detail: '' });
  const [supportedBy, setSupportedBy] = useState<EntitySummary[]>([]);
  const create = useMutation({ mutationFn: () => api.post(`/solutions/${solutionId}/claims`, { kind: f.kind, text: f.text, detail: f.detail || undefined, supportedBy: supportedBy.map((e) => ({ type: e.type, id: e.id })) }), onSuccess: onDone });
  return (
    <div className="rr-card rr-stack rr-stack--3">
      <Field label="Tipo" htmlFor="c-k"><Select id="c-k" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as ClaimKind })}>{CLAIM_KINDS.map((k) => <option key={k} value={k}>{CLAIM_KIND_LABELS[k]}</option>)}</Select></Field>
      <Field label="Afirmação" htmlFor="c-t"><Textarea id="c-t" rows={2} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} autoFocus /></Field>
      <Field label="Detalhe" htmlFor="c-d"><Textarea id="c-d" rows={2} value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} /></Field>
      <Field label="Sustentada por" htmlFor="c-s" hint={supportedBy.length ? supportedBy.map((e) => e.title).join(' · ') : 'evidências, observações, sinais, hipóteses, efeitos'}>
        <EntityPicker projectId={projectId} types={['evidence', 'observation', 'signal', 'hypothesis', 'effect', 'session']} exclude={supportedBy} onSelect={(e) => setSupportedBy([...supportedBy, e])} />
      </Field>
      {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
      <div className="rr-row"><Button size="sm" variant="primary" disabled={f.text.trim().length < 3 || create.isPending} onClick={() => create.mutate()}>adicionar</Button><Button size="sm" variant="quiet" onClick={onCancel}>cancelar</Button></div>
    </div>
  );
}

function ClaimCard({ c, projectId, canReview, canEdit }: { c: SolutionClaim; projectId: string; canReview: boolean; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const [showProv, setShowProv] = useState(false);
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/claims/${c.id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: () => api.delete(`/claims/${c.id}`), onSuccess: invalidate });
  return (
    <div className={cx('rr-claim', `rr-claim--${c.humanStatus}`)}>
      <div className="rr-row rr-row--between" style={{ alignItems: 'flex-start' }}>
        <div className="rr-stack rr-stack--1" style={{ flex: 1 }}>
          <p className="rr-claim__text">{c.text}</p>
          {c.detail && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>{c.detail}</p>}
          <button type="button" className="rr-claim__prov" onClick={() => setShowProv(!showProv)}>
            <span style={{ color: 'var(--rr-up)' }}>{c.supportedBy.length} sustentam</span>
            <span style={{ color: 'var(--rr-down)' }}>{c.contradictedBy.length} contradizem</span>
            {c.initiatives.length > 0 && <span>{c.initiatives.length} iniciativa(s)</span>}
            {c.confidence !== null && <span>confiança {Math.round(c.confidence * 100)}%</span>}
            <span>{showProv ? 'esconder' : 'ver proveniência'}</span>
          </button>
        </div>
        <div className="rr-row" style={{ flexShrink: 0 }}>
          <Tag tone={c.humanStatus === 'accepted' ? 'accent' : 'outline'}>{c.humanStatus === 'accepted' ? 'aceita' : c.humanStatus === 'rejected' ? 'rejeitada' : 'a revisar'}</Tag>
          {canReview && c.humanStatus !== 'accepted' && <Button size="sm" variant="primary" onClick={() => update.mutate({ humanStatus: 'accepted' })} disabled={update.isPending}>aceitar</Button>}
          {canReview && c.humanStatus !== 'rejected' && <Button size="sm" variant="quiet" onClick={() => update.mutate({ humanStatus: 'rejected' })} disabled={update.isPending}>rejeitar</Button>}
          {canEdit && c.generatedBy !== 'ai' && <Button size="sm" variant="quiet" onClick={() => remove.mutate()} disabled={remove.isPending}>remover</Button>}
        </div>
      </div>
      {showProv && (
        <div className="rr-claim__refs">
          {c.supportedBy.map((r) => <RefRow key={r.relationId} r={r} tone="up" />)}
          {c.contradictedBy.map((r) => <RefRow key={r.relationId} r={r} tone="down" />)}
          {c.supportedBy.length === 0 && c.contradictedBy.length === 0 && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>sem proveniência registrada. afirmação sem lastro é opinião.</span>}
          {c.initiatives.map((i) => <span key={i.id} className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>→ iniciativa: {i.title} ({i.status})</span>)}
        </div>
      )}
      {update.isError && <span className="rr-field__error">{(update.error as Error).message}</span>}
    </div>
  );
}

function RefRow({ r, tone }: { r: ClaimRef; tone: 'up' | 'down' }) {
  const href = r.type === 'hypothesis' ? `/hipoteses/${r.id}` : r.type === 'source' ? `/fontes/${r.id}` : r.sourceId ? `/fontes/${r.sourceId}` : null;
  return (
    <div className="rr-claim__ref">
      <span className={tone === 'up' ? 'rr-signal__icon--up' : 'rr-signal__icon--down'}>{tone === 'up' ? '↑' : '↓'}</span>
      <span className="rr-label">{ENTITY_TYPE_LABELS[r.type] ?? r.type}</span>
      {href ? <Link to={href}>{r.title}</Link> : <span>{r.title}</span>}
      {r.note ? <span className="rr-claim__quote">{r.note}</span> : r.text && r.text !== r.title && <span className="rr-faint">«{r.text.slice(0, 140)}{r.text.length > 140 ? '…' : ''}»</span>}
    </div>
  );
}
